import { useRouter } from "next/router";
import classNames from "classnames/bind";
import { AnimatePresence } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { TopLevelNavigation } from "@header";
import { easeOutQuad } from "@/core/easing";
import ArrowRight from "@/assets/svg/arrow-right.svg";
import DropdownMenu from "./DropdownMenu/DropdownMenu";
import { useHeader } from "@/providers/HeaderContext";
import { useViewSize } from "@/providers/ViewSizeContext";

import styles from "./HeaderDropdown.module.scss";
const cx = classNames.bind(styles);

type HeaderDropdownProps = TopLevelNavigation[number] & {
  active?: boolean;
  hideSearchTray: () => void;
};

export default function HeaderDropdown(props: HeaderDropdownProps) {
  const { label, active, linkGroups, hideSearchTray } = props;
  const [expanded, setExpanded] = useState(false);
  const { setDropdownExpanded, setExpandedMenu, setCanExpandDropdown, canExpandDropdown } = useHeader();
  const dropdownEl = useRef<HTMLDivElement>(null);
  const canExpandDropdownRef = useRef(canExpandDropdown);
  const { isTouchDevice } = useViewSize();
  const router = useRouter();
  const rootEl = useRef<HTMLDivElement>(null);
  const enterTimeout = useRef<NodeJS.Timeout | null>(null);
  const menuId = `header-dropdown-${label?.toLowerCase().replace(/[^a-z0-9]/g, '')}`;

  const handleClick = () => {
    if (isTouchDevice) {
      if (expanded) {
        collapseDropdown();
      } else expandDropdown();
    } else {
      const firstLink = rootEl.current?.querySelector<HTMLAnchorElement>("a");
      firstLink?.click();
      setCanExpandDropdown(false);
    }
  };

  const expandDropdown = () => {
    setExpanded(true);
    setDropdownExpanded(true);
  };

  const collapseDropdown = () => {
    setExpanded(false);
    setDropdownExpanded(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (expanded) {
        collapseDropdown();
      } else {
        expandDropdown();
      }
    } else if (event.key === "Escape") {
      collapseDropdown();
    }
  };

  const handleMouseEnter = () => {
    if (isTouchDevice || !canExpandDropdownRef.current) return;
    enterTimeout.current = setTimeout(() => {
      hideSearchTray();
      expandDropdown();
    }, 150);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    collapseDropdown();
  };

  const handleMouseLeave = () => {
    if (isTouchDevice) return;
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
      enterTimeout.current = null;
    }
    collapseDropdown();
  };
  const onMouseMove = () => {
    setTimeout(() => {
      setCanExpandDropdown(true);
      window.removeEventListener("mousemove", onMouseMove);
    }, 150);
  }

  const checkMouseMove = () => {
    if (isTouchDevice) return;
    if (!canExpandDropdownRef.current) window.addEventListener("mousemove", onMouseMove);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootEl.current && !rootEl.current.contains(event.target as Node) && expanded) {
        collapseDropdown();
      }
    };
    if (expanded && isTouchDevice) window.addEventListener("click", handleClickOutside);
    if (expanded) {
      setExpandedMenu(dropdownEl.current);
    } else {
      setExpandedMenu(null);
    }
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [expanded]);

  useEffect(() => {
    router.events.on("routeChangeStart", collapseDropdown);
    router.events.on("routeChangeComplete", checkMouseMove);
    return () => {
      router.events.off("routeChangeStart", collapseDropdown);
    };
  }, []);

  useEffect(() => {
    canExpandDropdownRef.current = canExpandDropdown;
  }, [canExpandDropdown]);

  return (
    <div
      className={cx("header-dropdown", { active, expanded })}
      onMouseLeave={handleMouseLeave}
      ref={rootEl}
    >
      <button
        className={cx("header-dropdown__button", { active, expanded })}
        data-gtm-ignore="true"
        onMouseEnter={handleMouseEnter}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        aria-controls={menuId}
        aria-expanded={expanded ? 'true' : 'false'}
      >
        <span className={cx(["label", "cta"])}>
          {label}
          <ArrowRight className={cx("arrow")} aria-hidden="true" />
        </span>
      </button>
      <AnimatePresence>
        {expanded && (
          <DropdownMenu
            ref={dropdownEl}
            initial={{ opacity: 0, filter: "blur(4px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, filter: "blur(4px)" }}
            transition={{ duration: 0.35, ease: easeOutQuad }}
            linkGroups={linkGroups}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            key={label}
            menuId={menuId}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
